/**
 * Tooltip Component
 *
 * Generic wrapper that shows a tooltip on hover.
 * Uses React Portal with fixed positioning to avoid clipping by overflow containers.
 * Follows the UI kit tooltip style (dark background, rounded, arrow).
 */

import React, { useState, useRef } from "react";
import { createPortal } from "react-dom";

interface TooltipProps {
  /** Tooltip content (text or JSX) */
  content: React.ReactNode;
  /** Element to wrap */
  children: React.ReactNode;
  /** Additional CSS classes for the wrapper span */
  className?: string;
}

interface TooltipPosition {
  top: number;
  left: number;
}

const isEmptyContent = (content: React.ReactNode): boolean =>
  content === null || content === undefined || content === false || content === "";

const Tooltip: React.FC<TooltipProps> = ({ content, children, className = "" }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [position, setPosition] = useState<TooltipPosition>({ top: 0, left: 0 });
  const wrapperRef = useRef<HTMLSpanElement>(null);

  const showTooltip = (): void => {
    if (wrapperRef.current != null) {
      const rect = wrapperRef.current.getBoundingClientRect();
      setPosition({
        top: rect.top - 8, // 8px gap above element
        left: rect.left + rect.width / 2, // center horizontally
      });
    }
    setIsVisible(true);
  };

  const hideTooltip = (): void => {
    setIsVisible(false);
  };

  // When there's nothing to say, behave as a transparent passthrough.
  if (isEmptyContent(content)) {
    return <>{children}</>;
  }

  const tooltip =
    isVisible &&
    createPortal(
      <div
        role="tooltip"
        style={{
          position: "fixed",
          top: position.top,
          left: position.left,
          transform: "translate(-50%, -100%)",
        }}
        className="z-[9999] px-3 py-2 text-sm text-white bg-gray-900 dark:bg-gray-700 rounded-lg shadow-lg pointer-events-none"
      >
        {content}
        {/* Arrow pointing down */}
        <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-gray-900 dark:border-t-gray-700" />
      </div>,
      document.body
    );

  return (
    // tabIndex makes the wrapper itself focusable so the tooltip is reachable
    // by keyboard and still appears even when the wrapped element is a disabled
    // button (disabled buttons emit no pointer/focus events of their own).
    <span
      ref={wrapperRef}
      tabIndex={0}
      className={`cursor-help ${className}`}
      onMouseEnter={showTooltip}
      onMouseLeave={hideTooltip}
      onFocus={showTooltip}
      onBlur={hideTooltip}
    >
      {children}
      {tooltip}
    </span>
  );
};

export default Tooltip;
